import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import { TablePlaceholder } from 'components/TablePlaceholder/TablePlaceholder';
import i18n from 'i18n';
import ToggleButtons from 'components/common/ToggleButtons/ToggleButtons';
import { CustomField } from 'components/common/form/field/CustomField';
import { SelectField } from 'components/common/form/field/SelectField';
import Select from 'components/common/Select/Select';
import { DateRangePicker } from 'components/common/DateRangePicker/DateRangePicker';
import { StickableTanstackTable } from 'components/common/Table/StickableTanstackTable';
import {
  TableChangeState,
  TableChangeType
} from 'components/common/Table/types';
import { getWeatherVerificationColumns } from './weatherVerificationColumns';
import {
  KEY_COLUMN,
  MAX_QUERY_DAYS,
  QUERY_WINDOW_MONTHS,
  SIZE_PER_PAGE,
  WeatherVerificationTabModelData,
  WeatherVerificationTableRecord
} from './WeatherVerificationTabModel';
import { WeatherVerificationSummaryCards } from './WeatherVerificationSummaryCards';
import styles from './weatherVerificationTab.module.scss';

type WeatherVerificationProps = {
  model: WeatherVerificationTabModelData;
  /** 訂單迄日，日期選擇器可選的上限；下限由 model 依可查詢範圍算出 */
  maxDate: string;
};

export const WeatherVerificationFilters: React.FC<WeatherVerificationProps> = ({
  model,
  maxDate
}) => {
  const {
    minQueryDate,
    draftFrom,
    draftTo,
    result,
    campaignId,
    storeKeys,
    resultFilterOptions,
    campaignOptions,
    storeOptions,
    dayRangeError,
    outOfQueryWindow,
    canQuery,
    onDateRangeChange,
    onDatePickerClosed,
    onResultChange,
    onCampaignChange,
    onStoreChange,
    onQueryBtnClick
  } = model;

  // 篩選列被卸載時日曆不會觸發關閉，由這裡捨棄沒確認的選取；
  // 用 ref 才能在卸載時呼叫到最新的 discardDatePicks
  const discardDatePicksRef = useRef(model.discardDatePicks);
  discardDatePicksRef.current = model.discardDatePicks;
  useEffect(() => () => discardDatePicksRef.current(), []);

  return (
    <>
      <CustomField
        label={i18n.t('weatherVerification.labels.timeSlot')}
        name='weatherVerificationTimeSlot'
      >
        <div className={styles.timeSlotFilter}>
          <ToggleButtons
            className={styles.timeSlotButtons}
            name='weatherVerificationResult'
            value={result}
            options={resultFilterOptions}
            onChange={onResultChange}
          />
          <div className={styles.dateRangeArea}>
            <DateRangePicker
              minDate={minQueryDate}
              maxDate={maxDate}
              startDate={draftFrom}
              endDate={draftTo}
              format='YYYY-MM-DD'
              showTimePicker={false}
              onChange={onDateRangeChange}
              onPopOverClosed={onDatePickerClosed}
              popOverClassName={styles.dateRangePicker}
              startDatePickerDisabled={outOfQueryWindow}
              endDatePickerDisabled={outOfQueryWindow}
            />
            {outOfQueryWindow ? null : dayRangeError ? (
              <span className={styles.errorHint}>{dayRangeError}</span>
            ) : (
              <span className={styles.hint}>
                {i18n.t('weatherVerification.labels.maxQueryDaysHint', {
                  days: MAX_QUERY_DAYS
                })}
              </span>
            )}
          </div>
        </div>
      </CustomField>
      <SelectField
        label={i18n.t('weatherVerification.labels.campaign')}
        name='weatherVerificationCampaign'
        className={styles.campaignSelect}
        options={campaignOptions}
        value={campaignId}
        simpleValue
        required
        placeholder={i18n.t('common.placeholder.pleaseSelect')}
        onChange={onCampaignChange}
      />
      <CustomField
        label={i18n.t('weatherVerification.labels.store')}
        name='weatherVerificationStore'
      >
        <Select
          className={styles.storeSelect}
          name='weatherVerificationStore'
          options={storeOptions}
          value={storeKeys}
          isMulti
          simpleValue
          isClearable
          maxMenuHeight={200}
          placeholder={i18n.t('common.placeholder.pleaseSelect')}
          onChange={onStoreChange}
        />
      </CustomField>
      <CustomField label='' name='weatherVerificationQuery'>
        <Button
          variant='primary'
          size='sm'
          onClick={onQueryBtnClick}
          disabled={!canQuery}
        >
          {i18n.t('report.buttons.search')}
        </Button>
      </CustomField>
    </>
  );
};

export const WeatherVerificationResult: React.FC<
  Pick<WeatherVerificationProps, 'model'>
> = ({ model }) => {
  const {
    outOfQueryWindow,
    campaign,
    campaignConditionSummary,
    campaignOptions,
    campaignsLoaded,
    tableData,
    totalSize,
    page,
    onPageChange,
    onExportCsv,
    canExportCsv,
    summary,
    summaryLoading,
    summaryError
  } = model;

  const columns = useMemo(() => getWeatherVerificationColumns(), []);

  const handleTableChange = useCallback(
    (
      type: TableChangeType,
      newState: TableChangeState<WeatherVerificationTableRecord>
    ) => {
      if (type === 'pagination') {
        onPageChange(newState.page);
      }
    },
    [onPageChange]
  );

  if (outOfQueryWindow) {
    return (
      <div className={styles.emptyState}>
        <TablePlaceholder
          placeholder={i18n.t(
            'weatherVerification.descriptions.queryWindowLimit',
            { months: QUERY_WINDOW_MONTHS }
          )}
        />
      </div>
    );
  }

  // 區間內沒有任何活動有可顯示的資料 —— 結果區整段換成空狀態，圖示與廣告活動列表無資料時一致
  if (campaignsLoaded && campaignOptions.length === 0) {
    return (
      <div className={styles.emptyState}>
        <TablePlaceholder
          placeholder={i18n.t(
            'weatherVerification.descriptions.noMatchingCampaign'
          )}
        />
      </div>
    );
  }

  return (
    <>
      <WeatherVerificationSummaryCards
        summary={summary}
        loading={summaryLoading}
        error={summaryError}
      />
      <div className={styles.exportRow}>
        <button
          type='button'
          className={styles.exportButton}
          onClick={onExportCsv}
          disabled={!canExportCsv}
        >
          <FontAwesomeIcon icon={faDownload} />
          {i18n.t('weatherVerification.labels.exportCsv')}
        </button>
      </div>
      {campaign && (
        <div className={styles.campaignHeader}>
          <span className={styles.campaignName}>{campaign.campaignName}</span>
          <span className={styles.campaignConditions}>
            {campaignConditionSummary}
          </span>
        </div>
      )}
      <StickableTanstackTable
        remote
        keyField={KEY_COLUMN}
        data={tableData}
        columns={columns}
        onTableChange={handleTableChange}
        noDataIndication={i18n.t('common.labels.noData')}
        pagination={{
          page,
          sizePerPage: SIZE_PER_PAGE,
          totalSize
        }}
      />
    </>
  );
};
